import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  Avatar,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArticleIcon from '@mui/icons-material/ArticleOutlined';
import HubIcon from '@mui/icons-material/HubOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2Outlined';
import ForumIcon from '@mui/icons-material/ForumOutlined';
import WebRoundedIcon from '@mui/icons-material/WebOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboardOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined';
// Child item icons
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import BoltIcon from '@mui/icons-material/BoltOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import PermMediaIcon from '@mui/icons-material/PermMediaOutlined';
import MovieIcon from '@mui/icons-material/MovieCreationOutlined';
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCardOutlined';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useBusinessStore } from '@/store/businessStore';
import { contentPackagesApi } from '@/api/contentPackages';
import { automationsApi } from '@/api/automations';
import { postsApi } from '@/api/posts';
import { mediaApi } from '@/api/media';
import { useBusinessScope } from '@/hooks/useBusinessScope';
import { PALETTE, initialsOf } from '@/theme/tokens';
import BrandLogo from '@/components/BrandLogo';

export const SIDEBAR_WIDTH = 248;

// IA: every existing route folded into Publications / Accounts / Settings groups.
const GROUPS = [
  {
    id: 'dashboard',
    labelKey: 'nav.dashboard',
    icon: SpaceDashboardIcon,
    color: '#3D5AFE',
    to: '/dashboard',
  },
  {
    id: 'publications',
    labelKey: 'nav.publications',
    icon: ArticleIcon,
    color: '#6C5CE7',
    children: [
      { labelKey: 'nav.posts', to: '/posts', icon: DescriptionIcon, color: '#3D5AFE' },
      { labelKey: 'nav.packages', to: '/packages', icon: Inventory2Icon, color: '#8B5CF6' },
      { labelKey: 'nav.automations', to: '/automations', icon: BoltIcon, color: '#F97316' },
      { labelKey: 'nav.calendar', to: '/calendar', icon: CalendarMonthIcon, color: '#EC4899' },
      { labelKey: 'nav.media', to: '/media', icon: PermMediaIcon, color: '#F59E0B' },
      // Announcement page, not a feature yet — see pages/video/VideoStudio.
      // It sits in Publications because that is what it will produce, and
      // putting it here now means the nav does not have to be relearnt on the
      // day it ships.
      { labelKey: 'nav.video', to: '/video', icon: MovieIcon, color: '#14B8A6' },
    ],
  },
  {
    id: 'engagement',
    labelKey: 'nav.engagement',
    icon: ForumIcon,
    color: '#14B8A6',
    to: '/engagement/inbox',
  },
  {
    id: 'landing',
    labelKey: 'nav.landingPages',
    icon: WebRoundedIcon,
    color: '#F43F5E',
    to: '/landing-pages',
  },
  {
    id: 'accounts',
    labelKey: 'nav.socialAccounts',
    icon: HubIcon,
    color: '#0EA5E9',
    to: '/social-connections',
  },
  {
    id: 'qr-codes',
    labelKey: 'nav.qrCodes',
    icon: QrCode2Icon,
    color: '#0D9488',
    to: '/qr-codes',
  },
  {
    id: 'agency',
    labelKey: 'nav.agency',
    icon: AutoAwesomeIcon,
    color: '#7C3AED',
    to: '/agency',
  },
  {
    id: 'settings',
    labelKey: 'nav.settings',
    icon: SettingsIcon,
    color: '#F97316',
    children: [
      // General settings (/settings) and Profile (/profile) are about the
      // signed-in user, not the workspace, so they live only in the account
      // menu under the avatar in the Topbar — listing them here as well
      // showed the same two links twice.
      // Posting schedule is per-business — it lives in Business settings → Schedule.
      { labelKey: 'nav.billing', to: '/billing', icon: CreditCardIcon, color: '#F59E0B' },
      // WeManage is admin-only and reached directly at /adminwemanage — never
      // advertised in the customer-facing nav.
    ],
  },
];

function pathMatches(current, to) {
  const base = to.split('?')[0];
  if (base === '/dashboard') return current === '/dashboard';
  return current === base || current.startsWith(base + '/');
}

// Query-aware active check so /packages?type=simple and ?type=automation
// highlight independently. 'simple' is treated as the default (no param).
function itemActive(location, to) {
  if (!pathMatches(location.pathname, to)) return false;
  const wantType = new URLSearchParams(to.split('?')[1] || '').get('type');
  if (!wantType) return true;
  const curType = new URLSearchParams(location.search).get('type');
  if (wantType === 'simple') return !curType || curType === 'simple';
  return curType === wantType;
}

export default function Sidebar({ onNavigate }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [now, setNow] = useState(dayjs());

  // Live counts shown as badges next to nav items.
  const { scopeParams, scopeKey } = useBusinessScope();
  const setCurrentBusiness = useBusinessStore((s) => s.setCurrentBusiness);

  // Clicking the account chip returns to the agency (All Projects) dashboard.
  const goToAgency = () => {
    setCurrentBusiness('');
    navigate('/dashboard');
    onNavigate?.();
  };
  const totalOf = (d) => d?.meta?.total ?? (d?.data?.length ?? 0);
  const { data: pkgData } = useQuery({
    queryKey: ['content-packages', { per_page: 1, scope: scopeKey }],
    queryFn: () => contentPackagesApi.list({ per_page: 1, ...scopeParams }),
    staleTime: 30000,
  });
  // All badges must respect the selected business, or they contradict the
  // dashboard (which is scoped). scopeKey MUST be in the queryKey, otherwise
  // the count is cached across business switches.
  const { data: postsData } = useQuery({
    queryKey: ['posts', { per_page: 1, scope: scopeKey }],
    queryFn: () => postsApi.list({ per_page: 1, ...scopeParams }),
    staleTime: 30000,
  });
  const { data: schedData } = useQuery({
    queryKey: ['posts', { per_page: 1, status: 'scheduled', scope: scopeKey }],
    queryFn: () => postsApi.list({ per_page: 1, status: 'scheduled', ...scopeParams }),
    staleTime: 30000,
  });
  const { data: mediaData } = useQuery({
    queryKey: ['media', { per_page: 1, scope: scopeKey }],
    queryFn: () => mediaApi.list({ per_page: 1, ...scopeParams }),
    staleTime: 30000,
  });
  const { data: autoData } = useQuery({
    queryKey: ['automations', { scope: scopeKey }],
    queryFn: () => automationsApi.list(scopeParams),
    staleTime: 30000,
  });
  const counts = {
    '/posts': totalOf(postsData),
    '/packages': totalOf(pkgData),
    '/automations': totalOf(autoData),
    '/calendar': totalOf(schedData),
    '/media': totalOf(mediaData),
  };

  // "Businesses" top-level menu → the businesses list page.
  const businessGroup = {
    id: 'businesses-menu',
    labelKey: 'nav.businesses',
    icon: StorefrontIcon,
    color: '#14B8A6',
    to: '/businesses',
  };
  const groups = [GROUPS[0], businessGroup, ...GROUPS.slice(1)];

  // Which group contains the active route -> open it by default
  const activeGroup = GROUPS.find(
    (g) => g.children && g.children.some((c) => c.to && itemActive(location, c.to))
  );
  const [open, setOpen] = useState(() => ({
    publications: true,
    accounts: activeGroup?.id === 'accounts',
    settings: activeGroup?.id === 'settings',
  }));

  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 30000);
    return () => clearInterval(t);
  }, []);

  const handleNavigate = (to) => {
    navigate(to);
    onNavigate?.();
  };

  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));

  const userName = user?.name || user?.email?.split('@')[0] || 'User';

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: (t) => t.tokens.secondaryNav,
        borderRight: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      {/* User chip card */}
      <Box sx={{ px: 2, pt: 2, pb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          onClick={goToAgency}
          title="Back to agency overview"
          sx={{
            p: 1.25,
            bgcolor: 'background.paper',
            borderRadius: 3,
            cursor: 'pointer',
            boxShadow: (t) => t.tokens.shadows.card,
            border: (t) => `1px solid ${t.tokens.cardBorder}`,
            transition: 'all .15s',
            '&:hover': { borderColor: 'primary.main', boxShadow: '0 6px 18px rgba(26,35,80,.12)' },
          }}
        >
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontWeight: 700 }}>
            {initialsOf(userName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 14, color: 'text.primary' }}>
              {userName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {now.format('hh:mm A')}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Grouped nav */}
      <List sx={{ px: 1.5, flex: 1, overflowY: 'auto' }}>
        {groups.map((g) => {
          const Icon = g.icon;
          if (!g.children) {
            const active = pathMatches(location.pathname, g.to);
            return (
              <ListItemButton
                key={g.id}
                onClick={() => handleNavigate(g.to)}
                sx={{
                  borderRadius: 2.5,
                  mb: 0.25,
                  py: 0.7,
                  bgcolor: active ? `${g.color}14` : 'transparent',
                  transition: 'background .15s',
                  '&:hover': { bgcolor: active ? `${g.color}18` : PALETTE.workspace },
                }}
              >
                <ListItemIcon sx={{ minWidth: 38 }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      // Inactive tiles are a whisper of the brand colour, not a
                      // block of it — with a dozen of them stacked, the old
                      // saturation made the rail read as a colour swatch chart.
                      bgcolor: active ? g.color : `${g.color}14`,
                      color: active ? '#fff' : g.color,
                      boxShadow: active ? `0 4px 10px -2px ${g.color}80` : 'none',
                      transition: 'all .15s',
                    }}
                  >
                    <Icon sx={{ fontSize: 17 }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={t(g.labelKey)}
                  primaryTypographyProps={{
                    fontWeight: active ? 700 : 600,
                    fontSize: '0.855rem',
                    // Dark label even when active. The filled tile already says
                    // "selected"; colouring the text too was a third signal
                    // saying the same thing, and it read worse doing it.
                    color: 'text.primary',
                  }}
                />
              </ListItemButton>
            );
          }
          return (
            <Box key={g.id}>
              <ListItemButton
                onClick={() => toggle(g.id)}
                sx={{
                  borderRadius: 2.5, mb: 0.25, py: 0.7,
                  transition: 'background .15s',
                  '&:hover': { bgcolor: PALETTE.workspace },
                }}
              >
                <ListItemIcon sx={{ minWidth: 38 }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: `${g.color}14`, color: g.color,
                    }}
                  >
                    <Icon sx={{ fontSize: 17 }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={t(g.labelKey)}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: '0.855rem' }}
                />
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    color: 'text.secondary',
                    transform: open[g.id] ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 160ms ease',
                  }}
                />
              </ListItemButton>
              <Collapse in={open[g.id]} unmountOnExit>
                <List disablePadding sx={{ pl: 2.5, mb: 0.5 }}>
                  {g.children.map((c) => {
                    const active = itemActive(location, c.to);
                    return (
                      <ListItemButton
                        key={c.to}
                        onClick={() => handleNavigate(c.to)}
                        sx={{
                          borderRadius: 2.5,
                          py: 0.45,
                          pl: 1,
                          mb: 0.15,
                          // No left border. The filled icon tile is already the
                          // selected marker; a rule down the side as well meant
                          // the same state was being announced three times over.
                          bgcolor: active ? `${c.color}14` : 'transparent',
                          transition: 'background .15s',
                          '&:hover': { bgcolor: active ? `${c.color}18` : PALETTE.workspace },
                        }}
                      >
                        {c.icon && (
                          <ListItemIcon sx={{ minWidth: 34 }}>
                            <Box
                              sx={{
                                width: 26, height: 26, borderRadius: 1.75,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: active ? c.color : `${c.color}14`,
                                color: active ? '#fff' : c.color,
                                boxShadow: active ? `0 3px 8px -1px ${c.color}80` : 'none',
                                transition: 'all .15s',
                              }}
                            >
                              <c.icon sx={{ fontSize: 15 }} />
                            </Box>
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={t(c.labelKey)}
                          primaryTypographyProps={{
                            fontWeight: active ? 700 : 500,
                            fontSize: '0.815rem',
                            color: 'text.primary',
                          }}
                        />
                        {counts[c.to] > 0 && (
                          <Box
                            sx={{
                              ml: 0.5, minWidth: 19, height: 17, px: 0.65, borderRadius: 999,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 10.5, fontWeight: 700, lineHeight: 1,
                              // Neutral until the row is selected. A dozen
                              // saturated count pills down the rail competed
                              // with the navigation itself for attention.
                              bgcolor: active ? c.color : PALETTE.workspace,
                              color: active ? '#fff' : PALETTE.muted,
                              transition: 'all .15s',
                            }}
                          >
                            {counts[c.to]}
                          </Box>
                        )}
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      {/*
        The only brand mark on the screen.

        Sits outside the scrolling <List> so it stays pinned to the bottom of
        the rail rather than drifting up when the nav is short or scrolling away
        when it's long. Full strength rather than muted, now that it isn't
        competing with a second copy above.
      */}
      <Box
        sx={{
          px: 2, py: 1.75, flexShrink: 0,
          borderTop: (t) => `1px solid ${t.palette.divider}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <BrandLogo height={21} />
      </Box>
    </Box>
  );
}
